import React from 'react';
import { Link } from 'react-router-dom';
import { Column } from '../../../components/DataTable/DataTable';
import { AppDuration } from './useData';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';

const formatMinutes = (minutes?: number): string => {
  if (minutes == null || Number.isNaN(minutes)) return '-';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (remainder === 0) return `${hours} hr`;
  return `${hours} hr ${remainder} min`;
};

export const moduleConfig = {
  module: {
    title: 'Appointment Durations',
    description: 'Manage appointment duration presets',
    i18nKey: 'duration',
    permissionModuleKey: 'app_duration',
    route: {
      list: '/duration',
      new: '/duration/new',
      detail: (id: number) => `/duration/${id}`,
      edit: (id: number) => `/duration/${id}/edit`,
    },
    api: {
      list: '/p-crm-duration',
      detail: (id: number) => `/p-crm-duration/${id}`,
    },
  },
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },
  search: {
    placeholder: 'Search appointment durations...',
  },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: ['pd_descr'],
    },
  ],
  filters: {
    fields: [
      {
        key: 'pd_descr',
        label: 'Description',
        placeholder: 'Type to filter description...',
        type: 'text' as const,
        getValue: (item: AppDuration) => item.pd_descr,
        options: [],
      },
    ],
    applyFilter: (items: AppDuration[], filters: { pd_descr: string[] | string }) => {
      return items.filter((item) => {
        const descrFilter = filters.pd_descr;
        if (typeof descrFilter === 'string' && descrFilter.trim() !== '') {
          const value = (item.pd_descr || '').toLowerCase();
          if (!value.includes(descrFilter.toLowerCase())) return false;
        } else if (Array.isArray(descrFilter) && descrFilter.length > 0) {
          const value = (item.pd_descr || '').toLowerCase();
          const matches = descrFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }
        return true;
      });
    },
  },
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Duration',
        showTitle: false,
        variant: 'primary' as const,
        href: '/duration/new',
      },
    ],
  },
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: AppDuration) => {
      window.location.href = `/duration/${item.id}`;
    },
    keyExtractor: (item: AppDuration) => item.id,
  },
  defaultSort: { key: 'pd_order', direction: 'asc' as const },
  emptyStates: {
    noData: 'Get started by creating a new appointment duration.',
    noSearchResults: 'No appointment durations found matching your search criteria.',
    noFilterResults: 'No appointment durations match the selected filters.',
  },
  delete: {
    title: 'Delete Duration',
    message: 'Are you sure you want to delete this duration? Appointments using it may need to be updated.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete duration. Please try again.',
    handler: async (id: number) => {
      const { deleteAppDuration } = await import('../../../services/mutations');
      await deleteAppDuration(id);
    },
  },
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
  },
  detail: {
    subtitle: 'Duration Details',
    notFoundTitle: 'Duration not found',
    notFoundMessage: 'The duration you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Appointment Durations',
    defaultTitle: 'Unnamed Duration',
    getTitle: (item: AppDuration) => {
      return item.pd_descr || `Duration #${item.id}`;
    },
  },
  form: {
    new: {
      title: 'New Duration',
      subtitle: 'Add a new appointment duration preset',
      submitText: 'Create Duration',
    },
    edit: {
      title: 'Edit Duration',
      subtitle: 'Update duration preset',
      submitText: 'Update Duration',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading duration...',
    errorMessage: 'Failed to save duration. Please try again.',
    create: async (data: Partial<AppDuration>) => {
      const { createAppDuration } = await import('../../../services/mutations');
      return await createAppDuration(data);
    },
    update: async (id: number, data: Partial<AppDuration>) => {
      const { updateAppDuration } = await import('../../../services/mutations');
      return await updateAppDuration(id, data);
    },
  },
  fields: [
    {
      name: 'pd_descr',
      label: 'Description',
      type: 'text',
      required: true,
      placeholder: 'e.g. 30 minutes',
      validation: {
        required: true,
        minLength: 1,
        maxLength: 255,
      },
    },
    {
      name: 'pd_min',
      label: 'Minutes',
      type: 'number',
      required: true,
      placeholder: '30',
      helperText: 'Length of the appointment in minutes.',
      validation: {
        required: true,
        min: 1,
      },
    },
    {
      name: 'pd_order',
      label: 'Order',
      type: 'number',
      required: false,
      placeholder: '0',
      helperText: 'Sort position in lists and dropdowns (lower numbers appear first).',
      validation: {
        min: 0,
      },
    },
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Duration Information',
        icon: '⏱',
        fields: ['pd_descr', 'pd_min', 'pd_order'],
        columns: 2,
        collapsible: false,
      },
    ],
  } as FormLayout,
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<AppDuration>[] => [
  {
    key: 'id',
    header: 'ID',
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-900">{value || '-'}</span>
    ),
  },
  {
    key: 'pd_descr',
    header: 'Description',
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-900">{value || '-'}</span>
    ),
  },
  {
    key: 'pd_min',
    header: 'Length',
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-700">{formatMinutes(value as number | undefined)}</span>
    ),
  },
  {
    key: 'pd_order',
    header: 'Order',
    sortable: true,
    render: (value) => (
      <span className="text-sm font-medium text-gray-900">{value ?? 0}</span>
    ),
  },
];

export const getDetailSections = (
  item: AppDuration,
  formatDate: (dateString?: string) => string
) => {
  return [
    {
      title: 'Basic Information',
      icon: '⏱',
      fields: [
        {
          label: 'Description',
          value: item.pd_descr || '-',
        },
        {
          label: 'Order',
          value: item.pd_order ?? 0,
        },
        {
          label: 'Minutes',
          value: item.pd_min ?? '-',
        },
        {
          label: 'Display',
          value: formatMinutes(item.pd_min),
        },
      ],
    },
  ];
};

export const getStatusBadges = (_item: AppDuration) => null;

export const getInitialFormData = (): Partial<AppDuration> => {
  return {
    pd_descr: '',
    pd_min: 30,
    pd_order: 0,
  };
};

export const mapItemToFormData = (item: AppDuration): Partial<AppDuration> => {
  return {
    pd_descr: item.pd_descr || '',
    pd_min: item.pd_min ?? 30,
    pd_order: item.pd_order ?? 0,
  };
};

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    default:
      return null;
  }
};

export const getTableActions = (
  item: AppDuration,
  onDelete: (id: number) => void,
  _a?: unknown,
  _b?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <div className="flex items-center space-x-2 flex-nowrap">
      <Link
        to={moduleConfig.module.route.detail(item.id)}
        className="p-2 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-lg transition-colors"
        onClick={(e) => e.stopPropagation()}
        title="View"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      </Link>
      {canEdit && (
        <Link
          to={moduleConfig.module.route.edit(item.id)}
          className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
          onClick={(e) => e.stopPropagation()}
          title="Edit"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </Link>
      )}
      {canDelete && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(item.id);
          }}
          className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
          title="Delete"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      )}
    </div>
  );
};
